/* Orders Client barrel export */

import { IOrdersClient } from './IOrdersClient';
import { OrdersClientApi } from './ordersClientApi';
import { OrdersClientMocked } from './ordersClientMocked';

// Re-export types
export * from './ordersTypes';
export * from './IOrdersClient';

/* ---- Configuration ---- */

import { shouldUseMockedClient } from '../../environment';

/**
 * Determines whether to use mocked clients.
 * Automatically uses mocked clients on localhost and ofstest5 environments.
 */
const useMocked = shouldUseMockedClient();

/* ---- Singleton Instance ---- */

/**
 * Orders client singleton instance.
 * Automatically uses mocked data on localhost and ofstest5 environments.
 * Import directly: `import { ordersClient } from 'app/services/api/orders';`
 */
export const client: IOrdersClient = useMocked
  ? new OrdersClientMocked()
  : new OrdersClientApi();

// Log which client is being used for debugging
console.log(`[OrderManagementClient] Using ${useMocked ? 'MOCKED' : 'REAL'} client on ${typeof window !== 'undefined' ? window.location.hostname : 'server'}`);
console.log('[OrderManagementClient] Client instance:', client.constructor.name);

/* ---- Factory Function (Backwards Compatibility) ---- */

/**
 * @deprecated Use `ordersClient` directly instead.
 * Get the OrdersClient instance.
 */
export function getOrdersClient(): IOrdersClient {
  return client;
}

/* ---- Backwards Compatibility ---- */

/**
 * @deprecated Use getOrdersClient() instead
 */
export class OrdersClient implements IOrdersClient {
  private client: IOrdersClient;

  constructor() {
    this.client = getOrdersClient();
  }

  // Delegate all methods to the underlying client
  getOrders: IOrdersClient['getOrders'] = (...args) => this.client.getOrders(...args);
  getOrder: IOrdersClient['getOrder'] = (...args) => this.client.getOrder(...args);
  getOrderByDeliveryDate: IOrdersClient['getOrderByDeliveryDate'] = (...args) => this.client.getOrderByDeliveryDate(...args);
  createOrder: IOrdersClient['createOrder'] = (...args) => this.client.createOrder(...args);
  updateOrder: IOrdersClient['updateOrder'] = (...args) => this.client.updateOrder(...args);
  deleteOrder: IOrdersClient['deleteOrder'] = (...args) => this.client.deleteOrder(...args);
  duplicateOrder: IOrdersClient['duplicateOrder'] = (...args) => this.client.duplicateOrder(...args);
  copyOrder: IOrdersClient['copyOrder'] = (...args) => this.client.copyOrder(...args);
  addOrderItem: IOrdersClient['addOrderItem'] = (...args) => this.client.addOrderItem(...args);
  deleteOrderItem: IOrdersClient['deleteOrderItem'] = (...args) => this.client.deleteOrderItem(...args);
  deleteOrderItems: IOrdersClient['deleteOrderItems'] = (...args) => this.client.deleteOrderItems(...args);
  deleteAllOrderItems: IOrdersClient['deleteAllOrderItems'] = (...args) => this.client.deleteAllOrderItems(...args);
  checkoutOrder: IOrdersClient['checkoutOrder'] = (...args) => this.client.checkoutOrder(...args);
  submitOrder: IOrdersClient['submitOrder'] = (...args) => this.client.submitOrder(...args);
  getOrderTypes: IOrdersClient['getOrderTypes'] = (...args) => this.client.getOrderTypes(...args);
  getDeliverySchedules: IOrdersClient['getDeliverySchedules'] = (...args) => this.client.getDeliverySchedules(...args);
  getSuppliers: IOrdersClient['getSuppliers'] = (...args) => this.client.getSuppliers(...args);
  getCategories: IOrdersClient['getCategories'] = (...args) => this.client.getCategories(...args);
  getOrderForm: IOrdersClient['getOrderForm'] = (...args) => this.client.getOrderForm(...args);
  saveOrderForm: IOrdersClient['saveOrderForm'] = (...args) => this.client.saveOrderForm(...args);
  downloadOrderForm: IOrdersClient['downloadOrderForm'] = (...args) => this.client.downloadOrderForm(...args);
  uploadOrderForm: IOrdersClient['uploadOrderForm'] = (...args) => this.client.uploadOrderForm(...args);
  downloadOrderSpreadsheet: IOrdersClient['downloadOrderSpreadsheet'] = (...args) => this.client.downloadOrderSpreadsheet(...args);
  uploadOrderSpreadsheet: IOrdersClient['uploadOrderSpreadsheet'] = (...args) => this.client.uploadOrderSpreadsheet(...args);
  getShippingDetails: IOrdersClient['getShippingDetails'] = (...args) => this.client.getShippingDetails(...args);
  setShippingDetails: IOrdersClient['setShippingDetails'] = (...args) => this.client.setShippingDetails(...args);
  getStoreContainerData: IOrdersClient['getStoreContainerData'] = (...args) => this.client.getStoreContainerData(...args);
  getOrderAudit: IOrdersClient['getOrderAudit'] = (...args) => this.client.getOrderAudit(...args);
  approveOrder: IOrdersClient['approveOrder'] = (...args) => this.client.approveOrder(...args);
  approveAmendment: IOrdersClient['approveAmendment'] = (...args) => this.client.approveAmendment(...args);
  rejectOrder: IOrdersClient['rejectOrder'] = (...args) => this.client.rejectOrder(...args);
  rejectAmendment: IOrdersClient['rejectAmendment'] = (...args) => this.client.rejectAmendment(...args);
  updateOrderDeliveryDate: IOrdersClient['updateOrderDeliveryDate'] = (...args) => this.client.updateOrderDeliveryDate(...args);
  // Items
  getItems: IOrdersClient['getItems'] = (...args) => this.client.getItems(...args);
  getItemsByFranchiseName: IOrdersClient['getItemsByFranchiseName'] = (...args) => this.client.getItemsByFranchiseName(...args);
  getItem: IOrdersClient['getItem'] = (...args) => this.client.getItem(...args);
  getItemByBarcode: IOrdersClient['getItemByBarcode'] = (...args) => this.client.getItemByBarcode(...args);
  getItemBySku: IOrdersClient['getItemBySku'] = (...args) => this.client.getItemBySku(...args);
  getItemDocuments: IOrdersClient['getItemDocuments'] = (...args) => this.client.getItemDocuments(...args);
  getOrderDocuments: IOrdersClient['getOrderDocuments'] = (...args) => this.client.getOrderDocuments(...args);
  getItemAttributes: IOrdersClient['getItemAttributes'] = (...args) => this.client.getItemAttributes(...args);
  updateItemAttributes: IOrdersClient['updateItemAttributes'] = (...args) => this.client.updateItemAttributes(...args);
  // Catalogues
  getCatalogues: IOrdersClient['getCatalogues'] = (...args) => this.client.getCatalogues(...args);
  getCatalogueRetailers: IOrdersClient['getCatalogueRetailers'] = (...args) => this.client.getCatalogueRetailers(...args);
  getCatalogueProducts: IOrdersClient['getCatalogueProducts'] = (...args) => this.client.getCatalogueProducts(...args);
  getCategoriesByCatalogue: IOrdersClient['getCategoriesByCatalogue'] = (...args) => this.client.getCategoriesByCatalogue(...args);
  downloadCatalogueExportAttributes: IOrdersClient['downloadCatalogueExportAttributes'] = (...args) => this.client.downloadCatalogueExportAttributes(...args);
  uploadCatalogueExportAttributes: IOrdersClient['uploadCatalogueExportAttributes'] = (...args) => this.client.uploadCatalogueExportAttributes(...args);
  // Currency
  getExchangeRates: IOrdersClient['getExchangeRates'] = (...args) => this.client.getExchangeRates(...args);
  updateExchangeRate: IOrdersClient['updateExchangeRate'] = (...args) => this.client.updateExchangeRate(...args);
  deleteExchangeRate: IOrdersClient['deleteExchangeRate'] = (...args) => this.client.deleteExchangeRate(...args);
  // News & Alerts
  getNews: IOrdersClient['getNews'] = (...args) => this.client.getNews(...args);
  getOutOfStockAlert: IOrdersClient['getOutOfStockAlert'] = (...args) => this.client.getOutOfStockAlert(...args);
  getUrgentAlerts: IOrdersClient['getUrgentAlerts'] = (...args) => this.client.getUrgentAlerts(...args);
  // Settings
  getDefaultOrderSettings: IOrdersClient['getDefaultOrderSettings'] = (...args) => this.client.getDefaultOrderSettings(...args);
  updateDefaultOrderSettings: IOrdersClient['updateDefaultOrderSettings'] = (...args) => this.client.updateDefaultOrderSettings(...args);
  getOnboardingDeliveryMethods: IOrdersClient['getOnboardingDeliveryMethods'] = (...args) => this.client.getOnboardingDeliveryMethods(...args);
  // Instances
  getInstance: IOrdersClient['getInstance'] = (...args) => this.client.getInstance(...args);
  getInstances: IOrdersClient['getInstances'] = (...args) => this.client.getInstances(...args);
  createInstance: IOrdersClient['createInstance'] = (...args) => this.client.createInstance(...args);
  updateInstance: IOrdersClient['updateInstance'] = (...args) => this.client.updateInstance(...args);
  // Markets
  getMarket: IOrdersClient['getMarket'] = (...args) => this.client.getMarket(...args);
  getMarkets: IOrdersClient['getMarkets'] = (...args) => this.client.getMarkets(...args);
  createMarket: IOrdersClient['createMarket'] = (...args) => this.client.createMarket(...args);
  updateMarket: IOrdersClient['updateMarket'] = (...args) => this.client.updateMarket(...args);
  // Market Order Types
  getMarketOrderType: IOrdersClient['getMarketOrderType'] = (...args) => this.client.getMarketOrderType(...args);
  getMarketOrderTypes: IOrdersClient['getMarketOrderTypes'] = (...args) => this.client.getMarketOrderTypes(...args);
  createMarketOrderType: IOrdersClient['createMarketOrderType'] = (...args) => this.client.createMarketOrderType(...args);
  updateMarketOrderType: IOrdersClient['updateMarketOrderType'] = (...args) => this.client.updateMarketOrderType(...args);
  // Market Suppliers
  getMarketSupplier: IOrdersClient['getMarketSupplier'] = (...args) => this.client.getMarketSupplier(...args);
  getMarketSuppliers: IOrdersClient['getMarketSuppliers'] = (...args) => this.client.getMarketSuppliers(...args);
  createMarketSupplier: IOrdersClient['createMarketSupplier'] = (...args) => this.client.createMarketSupplier(...args);
  updateMarketSupplier: IOrdersClient['updateMarketSupplier'] = (...args) => this.client.updateMarketSupplier(...args);
  // Market Catalogues
  getMarketCatalogue: IOrdersClient['getMarketCatalogue'] = (...args) => this.client.getMarketCatalogue(...args);
  getMarketCatalogues: IOrdersClient['getMarketCatalogues'] = (...args) => this.client.getMarketCatalogues(...args);
  createMarketCatalogue: IOrdersClient['createMarketCatalogue'] = (...args) => this.client.createMarketCatalogue(...args);
  updateMarketCatalogue: IOrdersClient['updateMarketCatalogue'] = (...args) => this.client.updateMarketCatalogue(...args);
  // Market Stores
  getMarketStore: IOrdersClient['getMarketStore'] = (...args) => this.client.getMarketStore(...args);
  getMarketStores: IOrdersClient['getMarketStores'] = (...args) => this.client.getMarketStores(...args);
  createMarketStore: IOrdersClient['createMarketStore'] = (...args) => this.client.createMarketStore(...args);
  updateMarketStore: IOrdersClient['updateMarketStore'] = (...args) => this.client.updateMarketStore(...args);
  downloadMarketStores: IOrdersClient['downloadMarketStores'] = (...args) => this.client.downloadMarketStores(...args);
  uploadMarketStores: IOrdersClient['uploadMarketStores'] = (...args) => this.client.uploadMarketStores(...args);
  // UOM Types
  getUomType: IOrdersClient['getUomType'] = (...args) => this.client.getUomType(...args);
  getUomTypes: IOrdersClient['getUomTypes'] = (...args) => this.client.getUomTypes(...args);
  createUomType: IOrdersClient['createUomType'] = (...args) => this.client.createUomType(...args);
  updateUomType: IOrdersClient['updateUomType'] = (...args) => this.client.updateUomType(...args);
  // Market Categories
  getMarketCategory: IOrdersClient['getMarketCategory'] = (...args) => this.client.getMarketCategory(...args);
  getMarketCategories: IOrdersClient['getMarketCategories'] = (...args) => this.client.getMarketCategories(...args);
  createMarketCategory: IOrdersClient['createMarketCategory'] = (...args) => this.client.createMarketCategory(...args);
  updateMarketCategory: IOrdersClient['updateMarketCategory'] = (...args) => this.client.updateMarketCategory(...args);
  // Market Items
  getMarketItem: IOrdersClient['getMarketItem'] = (...args) => this.client.getMarketItem(...args);
  getMarketItems: IOrdersClient['getMarketItems'] = (...args) => this.client.getMarketItems(...args);
  createMarketItem: IOrdersClient['createMarketItem'] = (...args) => this.client.createMarketItem(...args);
  updateMarketItem: IOrdersClient['updateMarketItem'] = (...args) => this.client.updateMarketItem(...args);
  downloadMarketItems: IOrdersClient['downloadMarketItems'] = (...args) => this.client.downloadMarketItems(...args);
  uploadMarketItems: IOrdersClient['uploadMarketItems'] = (...args) => this.client.uploadMarketItems(...args);
  // Autoship Templates
  createOrUpdateAutoshipTemplate: IOrdersClient['createOrUpdateAutoshipTemplate'] = (...args) => this.client.createOrUpdateAutoshipTemplate(...args);
  getAutoshipTemplates: IOrdersClient['getAutoshipTemplates'] = (...args) => this.client.getAutoshipTemplates(...args);
  getAutoshipTemplateById: IOrdersClient['getAutoshipTemplateById'] = (...args) => this.client.getAutoshipTemplateById(...args);
  getAutoshipTemplateNames: IOrdersClient['getAutoshipTemplateNames'] = (...args) => this.client.getAutoshipTemplateNames(...args);
  downloadItemsCsv: IOrdersClient['downloadItemsCsv'] = (...args) => this.client.downloadItemsCsv(...args);
  uploadTemplateItemsCsv: IOrdersClient['uploadTemplateItemsCsv'] = (...args) => this.client.uploadTemplateItemsCsv(...args);
  archiveTemplate: IOrdersClient['archiveTemplate'] = (...args) => this.client.archiveTemplate(...args);
  deleteTemplate: IOrdersClient['deleteTemplate'] = (...args) => this.client.deleteTemplate(...args);
  // Autoship Schedules
  createOrUpdateAutoshipSchedule: IOrdersClient['createOrUpdateAutoshipSchedule'] = (...args) => this.client.createOrUpdateAutoshipSchedule(...args);
  getAutoshipSchedules: IOrdersClient['getAutoshipSchedules'] = (...args) => this.client.getAutoshipSchedules(...args);
  getAutoshipScheduleById: IOrdersClient['getAutoshipScheduleById'] = (...args) => this.client.getAutoshipScheduleById(...args);
  deleteSchedule: IOrdersClient['deleteSchedule'] = (...args) => this.client.deleteSchedule(...args);
  getDeliverySchedulesForStores: IOrdersClient['getDeliverySchedulesForStores'] = (...args) => this.client.getDeliverySchedulesForStores(...args);
  getAutoshipReasons: IOrdersClient['getAutoshipReasons'] = (...args) => this.client.getAutoshipReasons(...args);
  // EDN Orders
  getOrderEdn: IOrdersClient['getOrderEdn'] = (...args) => this.client.getOrderEdn(...args);
  // ODS - Order Delivery Schedule
  getODS: IOrdersClient['getODS'] = (...args) => this.client.getODS(...args);
  exportODS: IOrdersClient['exportODS'] = (...args) => this.client.exportODS(...args);
  validateODS: IOrdersClient['validateODS'] = (...args) => this.client.validateODS(...args);
  importODS: IOrdersClient['importODS'] = (...args) => this.client.importODS(...args);
  updateBulkODSDate: IOrdersClient['updateBulkODSDate'] = (...args) => this.client.updateBulkODSDate(...args);
  deleteBulkODS: IOrdersClient['deleteBulkODS'] = (...args) => this.client.deleteBulkODS(...args);
  addODS: IOrdersClient['addODS'] = (...args) => this.client.addODS(...args);
  // Reports
  generateForecastOrdersReport: IOrdersClient['generateForecastOrdersReport'] = (...args) => this.client.generateForecastOrdersReport(...args);
}
